import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import type { RepresentativeFormState } from "../trapets-lookup-widget";
import { COUNTRY_OPTIONS } from "./country-options";
import { IdentifierFields } from "./identifier-fields";
import { T } from "./representative-fields.i18n";

/**
 * QL-654 — one representative's section. With a single representative it looks
 * exactly as before (no numbering, no remove); with several each is numbered
 * and can be removed.
 */
export function RepresentativeFields({
  rep,
  index,
  total,
  onChange,
  onRemove,
  error,
  disabled,
}: {
  rep: RepresentativeFormState;
  index: number;
  total: number;
  onChange: (patch: Partial<RepresentativeFormState>) => void;
  onRemove: () => void;
  error: boolean;
  disabled: boolean;
}) {
  const t = useT(T);
  const several = total > 1;
  // A registered representative is screened as they are on record: the server
  // matches only by party id / national id and never edits them, so a changed
  // identity would be refused. Change them under Related parties instead.
  const registered = rep.partyId !== null;
  const locked = disabled || registered;
  const prefix = index === 0 ? "trapets-rep" : `trapets-rep-${index + 1}`;
  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-medium">
          {several ? `${t.representativeSection} ${index + 1}` : t.representativeSection}
        </span>
        <span className="text-xs text-muted-foreground">{t.representativeChecked}</span>
        {several && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={onRemove}
            disabled={disabled}
          >
            {t.removeRepresentative}
          </Button>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-name`}>
            {t.name} <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${prefix}-name`}
            value={rep.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t.fullName}
            disabled={locked}
          />
        </div>
        {/* QL-62 — the representative's OWN country, defaulting to the client's. */}
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-country`}>
            {t.country} <span className="text-destructive">*</span>
          </Label>
          <Select
            value={rep.country}
            onValueChange={(country) => onChange({ country })}
            disabled={locked}
          >
            <SelectTrigger id={`${prefix}-country`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((opt) => (
                <SelectItem key={opt.code} value={opt.code}>
                  {opt.name} ({opt.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <IdentifierFields
          idPrefix={prefix}
          mode={rep.idMode}
          setMode={(idMode) => onChange({ idMode })}
          ssn={rep.ssn}
          setSSN={(ssn) => onChange({ ssn })}
          birthDate={rep.birthDate}
          setBirthDate={(birthDate) => onChange({ birthDate })}
          country={rep.country}
          disabled={locked}
        />
      </div>
      {registered && <p className="text-xs text-muted-foreground">{t.registeredRepresentative}</p>}
      {error && <p className="text-xs text-destructive">{t.representativeRequired}</p>}
    </div>
  );
}
